import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import {
  Button,
  Card,
  Eyebrow,
  Field,
  I,
  Input,
  PageHeader,
  Pill,
  RadioCard,
  RadioCardGroup,
  Row,
  Stack,
  Textarea,
  useDelayedLoading,
} from '@leanin/ui';
import { SaveBar } from '../../form/SaveBar';
import type { GroupDetail } from '../../../lib/views/groups';
import { useCircle } from '../../../lib/queries/groups';
import { useUpdateCircle } from '../../../lib/mutations/groups';
import { circleSaveErrorMessage } from '../circle-save-error';
import {
  AdminImagePanel,
  type AdminImagePanelHandle,
} from '../../group/admin/AdminImagePanel';
import { AdminFormPageSkeleton } from '../../group/admin/AdminFormPageSkeleton';
import { PlacePicker, type PlaceSelection } from '../../geo';

// Per-circle tags. Same input pattern as networks (free-form string[]),
// so circle leaders can describe their circle in their own words. The
// older curated-Topics field on circles was dropped (network-level
// tags + per-circle tags is the single tagging axis).

type Format = 'in_person' | 'virtual' | 'hybrid';
type Affiliated = 'yes' | 'no' | '';
type OpenToNew = 'yes' | 'no';

function initialAffiliated(circle: GroupDetail): Affiliated {
  if (circle.affiliatedWithCompany === true) return 'yes';
  if (circle.affiliatedWithCompany === false) return 'no';
  return '';
}

// "Open to new members?" round-trip:
//   - 'open' or 'request' → 'yes' (anyone can ask to join)
//   - 'invite_only'       → 'no'  (leaders pick the members)
// 'open' is deprecated (Start-a-Circle never picks it); selecting "Yes"
// from the admin form writes back 'request' to match the create flow.
function initialOpenToNew(circle: GroupDetail): OpenToNew {
  return circle.joinPolicy === 'invite_only' ? 'no' : 'yes';
}

type Props = {
  readonly circleId: string;
};

export function CircleOverviewSection({ circleId }: Props) {
  const detailQuery = useCircle(circleId);

  const detail = detailQuery.data;
  const showSkeleton = useDelayedLoading(
    detail === undefined || detail.access === 'visitor',
  );
  if (showSkeleton) {
    return <AdminFormPageSkeleton withAction cards={2} rowsPerCard={4} />;
  }
  if (detail === undefined || detail.access === 'visitor') return null;

  return <CircleOverviewForm circle={detail.circle} />;
}

type FormProps = {
  readonly circle: GroupDetail;
};

function CircleOverviewForm({ circle }: FormProps) {
  const tc = useTranslations('Circles');
  const updateMutation = useUpdateCircle();

  const [name, setName] = useState(circle.name);
  const [description, setDescription] = useState(circle.description ?? '');
  const [format, setFormat] = useState<Format | ''>(circle.format ?? '');
  // Structured place selection — replaces the legacy free-text `location`
  // column. Hydrated from the server's `place` view if set; the picker
  // trusts the parent's text and does not re-fetch.
  const initialPlace = circle.place ?? null;
  const [place, setPlace] = useState<PlaceSelection | null>(
    initialPlace === null
      ? null
      : {
          placeId: initialPlace.id,
          placeText: initialPlace.text,
          placeLat: initialPlace.lat,
          placeLng: initialPlace.lng,
          placeCountryCode: initialPlace.countryCode,
        },
  );
  // Tri-state affiliation pair. '' = legacy/unset; the radio renders empty.
  const [affiliated, setAffiliated] = useState<Affiliated>(
    initialAffiliated(circle),
  );
  const [company, setCompany] = useState(circle.affiliatedCompany ?? '');
  const [openToNew, setOpenToNew] = useState<OpenToNew>(initialOpenToNew(circle));
  const [tags, setTags] = useState<string[]>(circle.tags ?? []);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const imagesRef = useRef<AdminImagePanelHandle | null>(null);
  const [imagesPending, setImagesPending] = useState(false);
  const [committing, setCommitting] = useState(false);
  const isPending = updateMutation.isPending || committing;

  const textDirty =
    name.trim() !== circle.name.trim() ||
    description.trim() !== (circle.description ?? '').trim() ||
    (format === '' ? null : format) !== circle.format ||
    (place?.placeId ?? null) !== (initialPlace?.id ?? null) ||
    affiliated !== initialAffiliated(circle) ||
    (affiliated === 'yes' && company.trim() !== (circle.affiliatedCompany ?? '')) ||
    openToNew !== initialOpenToNew(circle) ||
    JSON.stringify(tags) !== JSON.stringify(circle.tags ?? []);
  const dirty = textDirty || imagesPending;

  const addTag = () => {
    const t = tagInput.trim();
    if (t.length > 0 && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSave = async () => {
    if (!dirty) return;
    setError(null);
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    if (trimmedName.length === 0) {
      setError(tc('edit.errorNameRequired'));
      return;
    }
    if (affiliated === 'yes' && company.trim().length === 0) {
      setError(tc('edit.errorCompanyRequired'));
      return;
    }
    if (imagesPending) {
      setCommitting(true);
      try {
        await imagesRef.current?.commit();
      } catch (err) {
        setError(err instanceof Error ? err.message : tc('edit.errorImageUpload'));
        setCommitting(false);
        return;
      }
      setCommitting(false);
    }
    if (!textDirty) return;
    // Translate the tri-state answer into the column pair the API validates
    // against `circles_affiliation_shape`. '' (legacy) → don't send → server
    // leaves the columns alone.
    const affiliationPatch =
      affiliated === 'yes'
        ? { affiliatedWithCompany: true, affiliatedCompany: company.trim() }
        : affiliated === 'no'
          ? { affiliatedWithCompany: false, affiliatedCompany: null }
          : {};
    updateMutation.mutate(
      {
        circleId: circle.id,
        name: trimmedName,
        description: trimmedDesc.length > 0 ? trimmedDesc : null,
        ...(format === '' ? {} : { format }),
        place,
        tags,
        // Match the Start-a-Circle mapping: Yes → request, No → invite_only.
        joinPolicy: openToNew === 'yes' ? 'request' : 'invite_only',
        ...affiliationPatch,
        expectedVersion: circle.version,
      },
      {
        onError: (err) =>
          setError(circleSaveErrorMessage(err, tc('edit.errorUpdate'), tc('errors.linkNotAllowed'))),
      },
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader title={tc('edit.title')} />

      <Card pad="flush">
        <AdminImagePanel
          ref={imagesRef}
          groupKind="circle"
          groupId={circle.id}
          groupName={name}
          currentCoverUrl={circle.imageUrl ?? null}
          coverEyebrow={tc('edit.coverEyebrow')}
          onPendingChange={setImagesPending}
        />

        <Stack p="xl" gap="lg">
          {/* "The basics" mirrors Step 01 of the Start-a-Circle form so the
              create and edit flows feel like the same shape. */}
          <Eyebrow>{tc('edit.basics')}</Eyebrow>
          <Field label={tc('edit.nameLabel')} required>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>

          <Field label={tc('edit.descriptionLabel')}>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={2000}
            />
          </Field>

          <Field
            label={tc('edit.locationLabel')}
            required
            description={tc('edit.locationDescription')}
          >
            <PlacePicker
              mode="city"
              value={place}
              onChange={setPlace}
              placeholder={tc('edit.locationPlaceholder')}
              disabled={isPending}
            />
          </Field>

          <Field label={tc('edit.affiliatedQuestion')} required>
            <RadioCardGroup
              value={affiliated}
              onValueChange={(v) => setAffiliated(v as Affiliated)}
              columns={2}
              aria-label={tc('edit.affiliatedQuestion')}
            >
              <RadioCard value="yes" title={tc('edit.yes')} />
              <RadioCard value="no" title={tc('edit.no')} />
            </RadioCardGroup>
          </Field>
          {affiliated === 'yes' ? (
            <Field label={tc('edit.whichCompany')} required>
              <Input
                placeholder={tc('edit.companyPlaceholder')}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={isPending}
              />
            </Field>
          ) : null}
        </Stack>
      </Card>

      <Card pad="default">
        <Stack gap="lg">
          {/* "How you'll meet" mirrors Step 02 of the Start-a-Circle form:
              Open-to-new-members (joinPolicy) + Format as a 3-up
              RadioCardGroup (Virtual / In-person / Hybrid). Tags ride along
              here per leader preference. */}
          <Eyebrow>{tc('edit.howYoullMeet')}</Eyebrow>
          <Field
            label={tc('edit.openQuestion')}
            required
            description={tc('edit.openDescription')}
          >
            <RadioCardGroup
              value={openToNew}
              onValueChange={(v) => setOpenToNew(v as OpenToNew)}
              columns={2}
              aria-label={tc('edit.openQuestion')}
            >
              <RadioCard value="yes" title={tc('edit.yes')} />
              <RadioCard value="no" title={tc('edit.no')} />
            </RadioCardGroup>
          </Field>
          <Field label={tc('edit.meetFormatLabel')} required>
            <RadioCardGroup
              value={format}
              onValueChange={(v) => setFormat(v as Format)}
              columns={3}
              aria-label={tc('edit.meetingFormatAria')}
            >
              <RadioCard value="virtual" icon={I.globe} title={tc('edit.formatVirtual')} />
              <RadioCard value="in_person" icon={I.pin} title={tc('edit.formatInPerson')} />
              <RadioCard value="hybrid" icon={I.users} title={tc('edit.formatHybrid')} />
            </RadioCardGroup>
          </Field>

          <Field
            label={tc('edit.tagsLabel')}
            description={tc('edit.tagsDescription')}
          >
            <Stack gap="sm">
              <Row gap="sm" align="center">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder={tc('edit.tagPlaceholder')}
                  style={{ maxWidth: 240 }}
                  disabled={isPending}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={I.plus}
                  onClick={addTag}
                  disabled={isPending || tagInput.trim().length === 0}
                >
                  {tc('edit.addTag')}
                </Button>
              </Row>
              {tags.length > 0 ? (
                <Row gap="sm" wrap align="center">
                  {tags.map((tag) => (
                    <Pill
                      key={tag}
                      tone="neutral"
                      onRemove={() => removeTag(tag)}
                      removeAriaLabel={tc('edit.removeTagAria', { tag })}
                    >
                      {tag}
                    </Pill>
                  ))}
                </Row>
              ) : null}
            </Stack>
          </Field>
        </Stack>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={isPending}
        error={error}
        onSave={handleSave}
      />
    </Stack>
  );
}
